/**
 * Promoción de dimensiones de producto a **tags** de Sentry.
 *
 * Card EiDX4uFN — `entry_channel` va en contexts, no en tags: Sentry no
 * puede agrupar por él. Hija de DSczmVq7 (dashboards).
 *
 * Por qué existe este módulo
 * --------------------------
 * En el modelo de datos de Sentry los contexts custom (`contexts.product_event`)
 * se **guardan pero no se indexan**: se ven en el detalle de un evento y no se
 * puede filtrar ni agrupar por ellos en Discover. Sólo son consultables los
 * tags y un set acotado de campos nativos (`message`, `environment`,
 * `release`, `level`, `user`).
 *
 * Por eso `count() groupby entry_channel` no era una query que Sentry pudiera
 * responder, y los dashboards 1 y 3 de DSczmVq7 devolvían vacío.
 *
 * Criterio de cardinalidad (contrato — ver change doc para el detalle)
 * -------------------------------------------------------------------
 * Los tags son un índice invertido: cada par `(key, value)` distinto es una
 * entrada nueva. Un tag de alta cardinalidad degrada el índice del proyecto
 * entero y encarece la factura. Una dimensión se promueve **sólo si** cumple
 * las cinco condiciones:
 *
 *   1. Dominio cerrado y enumerado en el repo (un `as const` / union type).
 *      Si no se puede escribir la lista, no es tag.
 *   2. ≤ 50 valores distintos por ambiente, y el conjunto crece sólo con un
 *      deploy — nunca con el tráfico.
 *   3. No deriva de input del usuario: ni texto libre, ni IDs, ni URLs, ni
 *      timestamps, ni medidas numéricas continuas.
 *   4. No es PII ni un pseudónimo de PII. Hashear no baja la cardinalidad ni
 *      quita el carácter identificatorio.
 *   5. Hay una query concreta que la necesita (un dashboard ya escrito que
 *      quiere `groupby` o filtrar por ella). "Por si acaso" no es criterio.
 *
 * Lo que no cumple las cinco se queda en `contexts.product_event`, que sigue
 * llevando el payload completo. Esto es **sumar tags, no mover datos**.
 *
 * Cómo se hace cumplir (un comentario no es un guard)
 * ---------------------------------------------------
 *   - `buildEventTags` itera la **allowlist**, no el payload: una prop nueva
 *     en cualquier call site no puede volverse tag por accidente.
 *   - `NEVER_TAG_KEYS` se filtra en runtime aunque alguien contamine la
 *     allowlist (ver `PROMOTABLE_TAG_KEYS`).
 *   - `eventTags.test.ts` afirma que las dos listas son disjuntas, así que
 *     meter `session_id` en la allowlist rompe CI.
 *
 * @see docs/changes/2026-10-03_eidx4ufn_entry_channel_tag.md
 * @see docs/architecture/observability.md §4 y §9
 */

/**
 * Dimensiones promovidas a tag. Orden alfabético para diffs estables.
 *
 * Agregar una clave acá es una decisión de cardinalidad: justificar contra
 * las cinco condiciones de arriba en el PR, y nombrar la query que la
 * necesita.
 *
 * | Tag                 | Dominio                                      | Query |
 * |---------------------|----------------------------------------------|-------|
 * | `entry_channel`     | 10 valores (`ENTRY_CHANNELS`, sync FE↔BE)    | §9.1, §9.3 agrupan por él |
 * | `is_authenticated`  | booleano — 2 valores                         | funnel anónimo vs logueado |
 * | `last_step_reached` | cerrado — steps del wizard                   | §5 drop-off de `wizard_abandoned` |
 * | `profile`           | `vis` \| `no_vis` \| `mixto`                 | segmentación VIS |
 * | `step_index`        | entero chico — índice de step                | §5 histograma de steps |
 * | `step_name`         | cerrado — `question_id` del wizard           | §5 drop-off por step |
 * | `wizard_variant`    | cerrado (`perfilador`; futuro `publicacion`) | §9.1 segmenta el funnel |
 *
 * `profile` hoy sólo lo emite el tracker GA4 (`apps/web/src/analytics/trackEvent.ts`
 * — otro helper con el mismo nombre y otro transporte), no este. Queda
 * allowlisteado forward-compatible: un key que nunca llega no cuesta nada,
 * porque el builder itera la allowlist y omite lo ausente.
 */
export const PRODUCT_EVENT_TAG_KEYS = [
  'entry_channel',
  'is_authenticated',
  'last_step_reached',
  'profile',
  'step_index',
  'step_name',
  'wizard_variant',
] as const;

/**
 * Claves que **nunca** pueden ser tag, por cardinalidad intrínseca o por ser
 * identificatorias. No es una lista de "lo que hoy no promovemos" (eso lo
 * resuelve la ausencia en la allowlist): es el guard de lo que no puede
 * cambiar de opinión sin una discusión de privacidad o de costo.
 *
 *   - `session_id` — uno por pestaña: crece con el tráfico (condición 2).
 *   - `user_id_hash` / `user_id_hashed` — uno por persona, y pseudónimo de
 *     PII (condiciones 2 y 4). Los dos spellings quedan prohibidos porque
 *     `observability.md` §4 usa `user_id_hashed` y el payload real usa
 *     `user_id_hash`: el drift de naming no debe abrir un agujero.
 *   - `first_utm_*` — los nombres de campaña los escribe marketing en la
 *     URL: dominio abierto (condiciones 1, 2 y 3).
 *   - `first_referrer`, `first_landing_path` — URLs, cardinalidad
 *     efectivamente infinita.
 *   - `query` — texto libre de búsqueda del usuario.
 *
 * `city`, `release`, `environment` y las medidas (`*_ms`, `steps_edited`) no
 * están acá a propósito: no se promueven por ausencia en la allowlist, pero
 * su exclusión sí podría revisarse (un vocabulario cerrado de ciudades sería
 * un tag legítimo; `environment`/`release` ya son campos nativos indexados
 * de Sentry y duplicarlos sería costo sin beneficio).
 */
export const NEVER_TAG_KEYS = [
  'first_landing_path',
  'first_referrer',
  'first_utm_campaign',
  'first_utm_content',
  'first_utm_medium',
  'first_utm_source',
  'first_utm_term',
  'query',
  'session_id',
  'user_id_hash',
  'user_id_hashed',
] as const;

/**
 * Máximo de caracteres que acepta el valor de un tag en Sentry. Los valores
 * allowlisteados son de dominio cerrado y no deberían acercarse, pero truncar
 * es seguro barato: un valor más largo haría que Sentry descarte el tag.
 */
const MAX_TAG_VALUE_LENGTH = 200;

/**
 * Allowlist efectiva: la allowlist menos la denylist. Derivada en module init
 * para que el guard no dependa de que nadie se equivoque al editar la
 * allowlist.
 */
export const PROMOTABLE_TAG_KEYS: readonly string[] =
  PRODUCT_EVENT_TAG_KEYS.filter(
    (key) => !(NEVER_TAG_KEYS as readonly string[]).includes(key),
  );

/** `true` si la clave está allowlisteada y no está prohibida. */
export function isPromotableTagKey(key: string): boolean {
  return PROMOTABLE_TAG_KEYS.includes(key);
}

/**
 * Normaliza un valor a lo que Sentry acepta como valor de tag: un string
 * acotado.
 *
 * Retorna `undefined` —y el caller omite la clave— para:
 *   - `null` / `undefined` / string vacío: no hay nada que indexar.
 *   - números no finitos (`NaN`, `±Infinity`): se serializarían como basura.
 *   - objetos, arrays, funciones, símbolos: un `[object Object]` como valor
 *     de tag es ruido no acotado.
 *
 * `false` y `0` **sí** se emiten: son valores legítimos de dominio cerrado
 * (un step index 0, un `is_authenticated=false`), no ausencia de dato.
 */
function coerceTagValue(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value.length > 0 ? value.slice(0, MAX_TAG_VALUE_LENGTH) : undefined;
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : undefined;
  }
  return undefined;
}

/**
 * Proyecta el payload de un evento de producto sobre las dimensiones
 * promovidas a tag.
 *
 * Itera `PROMOTABLE_TAG_KEYS` (no las claves del payload), así que el
 * resultado está acotado por construcción: una prop nueva en un call site no
 * puede convertirse en tag sin pasar por un diff que alguien revisa.
 *
 * Función pura y total — no lanza, no muta el payload. El `try/catch` de
 * `trackEvent` la envuelve igual, pero no depende de él.
 *
 * @param payload `{ ...common_props, ...props }` del evento.
 * @returns Mapa de tags listo para mergear en `Sentry.captureMessage`.
 */
export function buildEventTags(
  payload: Record<string, unknown>,
): Record<string, string> {
  const tags: Record<string, string> = {};
  for (const key of PROMOTABLE_TAG_KEYS) {
    const value = coerceTagValue(payload[key]);
    if (value !== undefined) tags[key] = value;
  }
  return tags;
}
